import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResult } from '../../../../shared/models/api-result.model';
import {
  CreateLateFeePolicy,
  LateFeePolicy,
  LateFeeType,
  normalizeLateFeeType,
  UpdateLateFeePolicy,
} from '../../../models/late-fee-policy.model';
import { LateFeePolicyService } from '../../../services/late-fee-policy.service';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';
import { ToolbarModule } from 'primeng/toolbar';

@Component({
  selector: 'app-late-fee-policy-create-or-update',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    ToggleSwitchModule,
    ProgressSpinnerModule,
    RouterLink,
    FormErrorComponent,
    FormServerErrorComponent,
    ToolbarModule,
  ],
  templateUrl: './late-fee-policy-create-or-update.component.html',
})
export class LateFeePolicyCreateOrUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private lateFeePolicyService = inject(LateFeePolicyService);
  private formGroupService = inject(FormGroupService);

  readonly lateFeeType = LateFeeType;
  typeOptions = this.lateFeePolicyService.getLateFeeTypeOptions();

  formGroup: FormGroup = this.fb.group({
    id: [null],
    name: [null, [Validators.required, Validators.maxLength(255)]],
    type: [LateFeeType.Percentage, [Validators.required]],
    value: [null, [Validators.required, Validators.min(0.01)]],
    graceDays: [0, [Validators.required, Validators.min(0)]],
    isTaxable: [false],
    isActive: [true],
  });

  lateFeePolicyId = signal<string | null>(null);
  loading = signal(false);

  constructor() {
    this.applyValueValidators(this.formGroup.get('type')!.value);

    this.formGroup.get('type')!.valueChanges.subscribe((type) => {
      this.applyValueValidators(type);
    });
  }

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) {
      this.lateFeePolicyId.set(id);
      this.loadData(id);
    }
  }

  isPercentage(): boolean {
    return this.formGroup.get('type')!.value === LateFeeType.Percentage;
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        const control = this.formGroup.get(key);
        control?.markAsTouched();
      });
      return;
    }

    const id = this.lateFeePolicyId();
    const body = this.formGroup.getRawValue();

    this.loading.set(true);

    let request$: Observable<ApiResult>;

    if (id) {
      request$ = this.lateFeePolicyService.update(
        id,
        body as UpdateLateFeePolicy
      );
    } else {
      delete body.id;
      delete body.isActive;
      request$ = this.lateFeePolicyService.create(body as CreateLateFeePolicy);
    }

    request$.subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Multa salva com sucesso',
          life: 2000,
        });

        this.router.navigate(['/late-fee-policies']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors?.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors?.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors?.length == 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao salvar a multa',
              life: 3000,
            });
          }
        }
      },
    });
  }

  private applyValueValidators(type: LateFeeType): void {
    const validators = [Validators.required, Validators.min(0.01)];

    if (type === LateFeeType.Percentage) {
      validators.push(Validators.max(100));
    }

    const control = this.formGroup.get('value')!;
    control.setValidators(validators);
    control.updateValueAndValidity();
  }

  private loadData(id: string): void {
    this.loading.set(true);

    this.lateFeePolicyService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const model = response.data as LateFeePolicy;
        this.formGroup.patchValue({
          ...model,
          type: normalizeLateFeeType(model.type),
        });
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar a multa',
            life: 3000,
          });
        }
      },
    });
  }
}
